import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  Divider,
  Popover,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Box,
  Typography,
  Stack,
  useTheme,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import CodeIcon from '@mui/icons-material/Code';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import BlockIcon from '@mui/icons-material/Block';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import TitleIcon from '@mui/icons-material/Title';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import SubscriptIcon from '@mui/icons-material/Subscript';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import DataObjectIcon from '@mui/icons-material/DataObject';
import './TipTapEditor.css';

const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];

const TEXT_COLORS = {
  light: [
    { label: 'Default', value: null },
    { label: 'Black', value: '#000000' },
    { label: 'Dark gray', value: '#4b5563' },
    { label: 'Gray', value: '#9ca3af' },
    { label: 'Red', value: '#ef4444' },
    { label: 'Orange', value: '#f97316' },
    { label: 'Yellow', value: '#eab308' },
    { label: 'Green', value: '#22c55e' },
    { label: 'Blue', value: '#3b82f6' },
    { label: 'Purple', value: '#8b5cf6' },
    { label: 'Pink', value: '#ec4899' },
    { label: 'Teal', value: '#14b8a6' },
  ],
  dark: [
    { label: 'Default', value: null },
    { label: 'White', value: '#fafafa' },
    { label: 'Light gray', value: '#d4d4d8' },
    { label: 'Gray', value: '#a1a1aa' },
    { label: 'Red', value: '#f87171' },
    { label: 'Orange', value: '#fb923c' },
    { label: 'Yellow', value: '#facc15' },
    { label: 'Green', value: '#4ade80' },
    { label: 'Blue', value: '#60a5fa' },
    { label: 'Purple', value: '#a78bfa' },
    { label: 'Pink', value: '#f472b6' },
    { label: 'Teal', value: '#2dd4bf' },
  ],
};

const HIGHLIGHT_COLORS = {
  light: [
    { label: 'None', value: null },
    { label: 'Yellow', value: '#fef08a' },
    { label: 'Green', value: '#bbf7d0' },
    { label: 'Blue', value: '#bfdbfe' },
    { label: 'Purple', value: '#e9d5ff' },
    { label: 'Pink', value: '#fbcfe8' },
    { label: 'Orange', value: '#fed7aa' },
    { label: 'Red', value: '#fecaca' },
    { label: 'Gray', value: '#e5e7eb' },
  ],
  dark: [
    { label: 'None', value: null },
    { label: 'Yellow', value: '#854d0e' },
    { label: 'Green', value: '#166534' },
    { label: 'Blue', value: '#1e3a5f' },
    { label: 'Purple', value: '#4c1d95' },
    { label: 'Pink', value: '#831843' },
    { label: 'Orange', value: '#7c2d12' },
    { label: 'Red', value: '#7f1d1d' },
    { label: 'Gray', value: '#374151' },
  ],
};

const CELL_COLORS = {
  light: [
    { label: 'None', value: null },
    { label: 'Light gray', value: '#f3f4f6' },
    { label: 'Light blue', value: '#dbeafe' },
    { label: 'Light green', value: '#dcfce7' },
    { label: 'Light yellow', value: '#fef9c3' },
    { label: 'Light orange', value: '#ffedd5' },
    { label: 'Light red', value: '#fee2e2' },
    { label: 'Light purple', value: '#f3e8ff' },
    { label: 'Light pink', value: '#fce7f3' },
  ],
  dark: [
    { label: 'None', value: null },
    { label: 'Dark gray', value: '#374151' },
    { label: 'Dark blue', value: '#1e3a5f' },
    { label: 'Dark green', value: '#14532d' },
    { label: 'Dark yellow', value: '#713f12' },
    { label: 'Dark orange', value: '#7c2d12' },
    { label: 'Dark red', value: '#7f1d1d' },
    { label: 'Dark purple', value: '#3b0764' },
    { label: 'Dark pink', value: '#831843' },
  ],
};

const HEADINGS = [
  { label: 'Normal text', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
  { label: 'Heading 4', level: 4 },
];

const btnSx = {
  width: 30,
  height: 30,
  borderRadius: '6px',
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover' },
  '&.Mui-disabled': { opacity: 0.35 },
};

const activeBtnSx = {
  ...btnSx,
  bgcolor: 'primary.main',
  color: '#fff',
  '&:hover': { bgcolor: 'primary.dark' },
};

const TBButton = ({ onClick, active, disabled, title, children }) => (
  <Tooltip title={title} placement="top" enterDelay={400}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={active ? activeBtnSx : btnSx}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const TBDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
);

// --- Color swatch grid used by text color, highlight, and cell color ---
const ColorSwatchGrid = ({ colors, onSelect, onClose }) => (
  <Box sx={{ px: 1.5, py: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: 180 }}>
    {colors.map(({ label, value }) => (
      <Tooltip key={label} title={label} placement="top" enterDelay={300}>
        <Box
          onClick={() => { onSelect(value); onClose(); }}
          sx={{
            width: 22, height: 22,
            borderRadius: '4px', cursor: 'pointer',
            border: '1.5px solid', borderColor: 'divider',
            bgcolor: value || 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { transform: 'scale(1.2)', boxShadow: 1 },
            transition: 'all 0.1s ease',
          }}
        >
          {!value && <BlockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />}
        </Box>
      </Tooltip>
    ))}
  </Box>
);

// --- Link popover ---
const LinkPopover = ({ anchorEl, editor, onClose }) => {
  const [url, setUrl] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (anchorEl && editor) {
      const current = editor.isActive('link') ? editor.getAttributes('link').href || '' : '';
      setUrl(current);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [anchorEl, editor]);

  const handleApply = () => {
    if (!url.trim()) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
    }
    onClose();
  };

  const handleRemove = () => {
    editor.chain().focus().unsetLink().run();
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleApply(); }
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  const isEditing = editor?.isActive('link');

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, boxShadow: 3, width: 360 } } }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          {isEditing ? 'Edit Link' : 'Insert Link'}
        </Typography>
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="https://example.com or ${placeholder}"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {isEditing && (
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
              onClick={handleRemove}
              sx={{ mr: 'auto' }}
            >
              Remove
            </Button>
          )}
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={handleApply}>
            {isEditing ? 'Update' : 'Apply'}
          </Button>
        </Stack>
      </Box>
    </Popover>
  );
};

// --- Table menu ---
const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', px: 2, pt: 1.5, pb: 0.5 }}
  >
    {children}
  </Typography>
);

const TableMenu = ({ anchorEl, editor, onClose }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const isInTable = editor?.isActive('table');
  const [hoverCell, setHoverCell] = useState({ r: 0, c: 0 });

  const insert = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    onClose();
  };

  const run = (cmd) => () => { cmd(); onClose(); };
  const iconSx = { fontSize: 18, mr: 1, color: 'text.secondary' };

  return (
    <Menu
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => { setHoverCell({ r: 0, c: 0 }); onClose(); }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200, maxHeight: 480, overflowY: 'auto' } } }}
    >
      {!isInTable && (
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {hoverCell.r > 0 ? `${hoverCell.r} × ${hoverCell.c} table` : 'Insert table'}
          </Typography>
          <Box
            onMouseLeave={() => setHoverCell({ r: 0, c: 0 })}
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 24px)', gap: '3px', mt: 1 }}
          >
            {[1, 2, 3, 4, 5].map((r) =>
              [1, 2, 3, 4, 5].map((c) => {
                const highlighted = r <= hoverCell.r && c <= hoverCell.c;
                return (
                  <Box
                    key={`${r}-${c}`}
                    onMouseEnter={() => setHoverCell({ r, c })}
                    onClick={() => insert(r, c)}
                    sx={{
                      width: 24, height: 24,
                      border: '1px solid',
                      borderColor: highlighted ? 'primary.main' : 'divider',
                      bgcolor: highlighted ? 'primary.main' : 'transparent',
                      borderRadius: '3px', cursor: 'pointer',
                      transition: 'all 0.1s ease',
                    }}
                  />
                );
              })
            )}
          </Box>
        </Box>
      )}

      {isInTable && [
        <SectionLabel key="row-label">Rows</SectionLabel>,
        <MenuItem key="add-row-before" dense onClick={run(() => editor.chain().focus().addRowBefore().run())}>
          <AddIcon sx={iconSx} /><ListItemText>Insert row above</ListItemText>
        </MenuItem>,
        <MenuItem key="add-row-after" dense onClick={run(() => editor.chain().focus().addRowAfter().run())}>
          <AddIcon sx={iconSx} /><ListItemText>Insert row below</ListItemText>
        </MenuItem>,
        <MenuItem key="del-row" dense onClick={run(() => editor.chain().focus().deleteRow().run())}>
          <TableRowsIcon sx={{ ...iconSx, color: 'error.main' }} /><ListItemText sx={{ color: 'error.main' }}>Delete row</ListItemText>
        </MenuItem>,

        <SectionLabel key="col-label">Columns</SectionLabel>,
        <MenuItem key="add-col-before" dense onClick={run(() => editor.chain().focus().addColumnBefore().run())}>
          <AddIcon sx={iconSx} /><ListItemText>Insert column left</ListItemText>
        </MenuItem>,
        <MenuItem key="add-col-after" dense onClick={run(() => editor.chain().focus().addColumnAfter().run())}>
          <AddIcon sx={iconSx} /><ListItemText>Insert column right</ListItemText>
        </MenuItem>,
        <MenuItem key="del-col" dense onClick={run(() => editor.chain().focus().deleteColumn().run())}>
          <ViewColumnIcon sx={{ ...iconSx, color: 'error.main' }} /><ListItemText sx={{ color: 'error.main' }}>Delete column</ListItemText>
        </MenuItem>,

        <SectionLabel key="cell-label">Cells</SectionLabel>,
        <MenuItem key="merge" dense onClick={run(() => editor.chain().focus().mergeCells().run())} disabled={!editor.can().mergeCells()}>
          <CallMergeIcon sx={iconSx} /><ListItemText>Merge cells</ListItemText>
        </MenuItem>,
        <MenuItem key="split" dense onClick={run(() => editor.chain().focus().splitCell().run())} disabled={!editor.can().splitCell()}>
          <CallSplitIcon sx={iconSx} /><ListItemText>Split cell</ListItemText>
        </MenuItem>,
        <MenuItem key="toggle-header" dense onClick={run(() => editor.chain().focus().toggleHeaderRow().run())}>
          <TableRowsIcon sx={iconSx} /><ListItemText>Toggle header row</ListItemText>
        </MenuItem>,

        <SectionLabel key="color-label">Cell color</SectionLabel>,
        <ColorSwatchGrid
          key="color-grid"
          colors={CELL_COLORS[mode]}
          onSelect={(color) => editor.chain().focus().setCellAttribute('backgroundColor', color).run()}
          onClose={onClose}
        />,

        <Divider key="div-del" />,
        <MenuItem key="del-table" dense onClick={run(() => editor.chain().focus().deleteTable().run())}>
          <DeleteOutlineIcon sx={{ ...iconSx, color: 'error.main' }} /><ListItemText sx={{ color: 'error.main' }}>Delete table</ListItemText>
        </MenuItem>,
      ]}
    </Menu>
  );
};

// === Main toolbar ===
const EditorToolbar = ({ editor, onImageUpload, onSourceToggle, isSourceMode }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const fileInputRef = useRef(null);
  const [linkAnchor, setLinkAnchor] = useState(null);
  const [tableAnchor, setTableAnchor] = useState(null);
  const [fontAnchor, setFontAnchor] = useState(null);
  const [headingAnchor, setHeadingAnchor] = useState(null);
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);

  const handleImageClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) await onImageUpload(file, editor);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [editor, onImageUpload]);

  if (!editor) return null;

  const iconSize = { fontSize: 18 };

  const currentTextColor = editor.getAttributes('textStyle').color || null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '2px',
        px: 1,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.default',
      }}
    >
      {/* Source toggle */}
      <TBButton onClick={onSourceToggle} active={isSourceMode} title="Source code">
        <CodeIcon sx={iconSize} />
      </TBButton>

      <TBDivider />

      {/* Heading dropdown */}
      <TBButton
        onClick={(e) => setHeadingAnchor(e.currentTarget)}
        active={editor.isActive('heading')}
        disabled={isSourceMode}
        title="Heading"
      >
        <TitleIcon sx={iconSize} />
      </TBButton>
      <Menu
        open={Boolean(headingAnchor)}
        anchorEl={headingAnchor}
        onClose={() => setHeadingAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 160 } } }}
      >
        {HEADINGS.map(({ label, level }) => (
          <MenuItem
            key={level}
            dense
            selected={level === 0 ? !editor.isActive('heading') : editor.isActive('heading', { level })}
            onClick={() => {
              if (level === 0) {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level }).run();
              }
              setHeadingAnchor(null);
            }}
          >
            <ListItemText>
              <Typography sx={{ fontSize: level === 0 ? 14 : Math.max(14, 24 - level * 3), fontWeight: level > 0 ? 600 : 400 }}>
                {label}
              </Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Font size */}
      <TBButton onClick={(e) => setFontAnchor(e.currentTarget)} disabled={isSourceMode} title="Font size">
        <FormatSizeIcon sx={iconSize} />
      </TBButton>
      <Menu
        open={Boolean(fontAnchor)}
        anchorEl={fontAnchor}
        onClose={() => setFontAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 100, maxHeight: 300 } } }}
      >
        {FONT_SIZES.map((size) => (
          <MenuItem
            key={size}
            dense
            onClick={() => { editor.chain().focus().setFontSize(size).run(); setFontAnchor(null); }}
          >
            <ListItemText>{size}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <TBDivider />

      {/* Basic formatting */}
      <TBButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} disabled={isSourceMode} title="Bold">
        <FormatBoldIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} disabled={isSourceMode} title="Italic">
        <FormatItalicIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} disabled={isSourceMode} title="Underline">
        <FormatUnderlinedIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} disabled={isSourceMode} title="Strikethrough">
        <StrikethroughSIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleSubscript().run()} active={editor.isActive('subscript')} disabled={isSourceMode} title="Subscript">
        <SubscriptIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleSuperscript().run()} active={editor.isActive('superscript')} disabled={isSourceMode} title="Superscript">
        <SuperscriptIcon sx={iconSize} />
      </TBButton>

      <TBDivider />

      {/* Text color */}
      <TBButton
        onClick={(e) => setTextColorAnchor(e.currentTarget)}
        active={Boolean(currentTextColor)}
        disabled={isSourceMode}
        title="Text color"
      >
        <FormatColorTextIcon sx={{ ...iconSize, color: currentTextColor || undefined }} />
      </TBButton>
      <Popover
        open={Boolean(textColorAnchor)}
        anchorEl={textColorAnchor}
        onClose={() => setTextColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, boxShadow: 3 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', px: 1.5, pt: 1, pb: 0.5 }}>
          Text color
        </Typography>
        <ColorSwatchGrid
          colors={TEXT_COLORS[mode]}
          onSelect={(color) => {
            if (color) {
              editor.chain().focus().setColor(color).run();
            } else {
              editor.chain().focus().unsetColor().run();
            }
          }}
          onClose={() => setTextColorAnchor(null)}
        />
      </Popover>

      {/* Highlight */}
      <TBButton
        onClick={(e) => setHighlightAnchor(e.currentTarget)}
        active={editor.isActive('highlight')}
        disabled={isSourceMode}
        title="Highlight"
      >
        <BorderColorIcon sx={iconSize} />
      </TBButton>
      <Popover
        open={Boolean(highlightAnchor)}
        anchorEl={highlightAnchor}
        onClose={() => setHighlightAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, boxShadow: 3 } } }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', px: 1.5, pt: 1, pb: 0.5 }}>
          Highlight
        </Typography>
        <ColorSwatchGrid
          colors={HIGHLIGHT_COLORS[mode]}
          onSelect={(color) => {
            if (color) {
              editor.chain().focus().setHighlight({ color }).run();
            } else {
              editor.chain().focus().unsetHighlight().run();
            }
          }}
          onClose={() => setHighlightAnchor(null)}
        />
      </Popover>

      <TBDivider />

      {/* Lists & blocks */}
      <TBButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} disabled={isSourceMode} title="Bullet list">
        <FormatListBulletedIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} disabled={isSourceMode} title="Numbered list">
        <FormatListNumberedIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} disabled={isSourceMode} title="Blockquote">
        <FormatQuoteIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} disabled={isSourceMode} title="Code block">
        <DataObjectIcon sx={iconSize} />
      </TBButton>

      <TBDivider />

      {/* Alignment */}
      <TBButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} disabled={isSourceMode} title="Align left">
        <FormatAlignLeftIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} disabled={isSourceMode} title="Align center">
        <FormatAlignCenterIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} disabled={isSourceMode} title="Align right">
        <FormatAlignRightIcon sx={iconSize} />
      </TBButton>

      <TBDivider />

      {/* Insert controls */}
      <TBButton onClick={handleImageClick} disabled={isSourceMode} title="Insert image">
        <ImageIcon sx={iconSize} />
      </TBButton>
      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />

      <TBButton onClick={(e) => setTableAnchor(e.currentTarget)} active={editor.isActive('table')} disabled={isSourceMode} title="Table">
        <TableChartIcon sx={iconSize} />
      </TBButton>
      <TableMenu anchorEl={tableAnchor} editor={editor} onClose={() => setTableAnchor(null)} />

      <TBButton onClick={(e) => setLinkAnchor(e.currentTarget)} active={editor.isActive('link')} disabled={isSourceMode} title={editor.isActive('link') ? 'Edit link' : 'Insert link'}>
        <LinkIcon sx={iconSize} />
      </TBButton>
      <LinkPopover anchorEl={linkAnchor} editor={editor} onClose={() => setLinkAnchor(null)} />

      <TBButton onClick={() => editor.chain().focus().setHorizontalRule().run()} disabled={isSourceMode} title="Horizontal line">
        <HorizontalRuleIcon sx={iconSize} />
      </TBButton>

      <TBDivider />

      {/* Clear / undo / redo */}
      <TBButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} disabled={isSourceMode} title="Clear formatting">
        <FormatClearIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo() || isSourceMode} title="Undo">
        <UndoIcon sx={iconSize} />
      </TBButton>
      <TBButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo() || isSourceMode} title="Redo">
        <RedoIcon sx={iconSize} />
      </TBButton>
    </Box>
  );
};

export default EditorToolbar;
